import { useFindMany, useGlobalAction } from "@gadgetinc/react";
import { EllipsisHorizontalIcon, PlusIcon } from "@heroicons/react/16/solid";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "../../api";
import {
  WhatsappTemplateButtonTypeEnum,
  WhatsappTemplateCategoryEnum,
} from "../../enums";
import { OverflowFrame, PageHeading } from "../../externalComponents";
import LoadingSpinner from "../../externalComponents/LoadingSpinner";
import { Badge } from "../../externalComponents/tailwind/badge";
import { Button } from "../../externalComponents/tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../externalComponents/tailwind/dialog";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "../../externalComponents/tailwind/dropdown";
import EmptyState from "../../externalComponents/tailwind/empty-state";
import { Link } from "../../externalComponents/tailwind/link";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../externalComponents/tailwind/table";
import { TemplatePreview } from "../../externalComponents/TemplatePreview";
import { createScenarioFromScratch } from "../../functions/createScenarioFromScratch";
import { CustomerContext } from "../../providers";

export default function TemplatesPage() {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const campaignId = urlParams.get("campaign") ?? "";
  const campaignName = urlParams.get("campaignName") ?? "";
  const automationId = urlParams.get("automation") ?? "";
  const automationName = urlParams.get("automationName") ?? "";
  const nextPage = urlParams.get("next_page") ?? "";
  const scenarioId = urlParams.get("scenario") ?? "";
  const selectedNodeId = urlParams.get("selectedNodeId") ?? "";

  const [pageStatus, setPageStatus] = useState("");

  useEffect(() => {
    if (!nextPage) {
      setPageStatus("newCampaign");
    } else if (nextPage === "newTemplate") {
      setPageStatus("insertTemplate");
    } else if (nextPage === "campaigns" && campaignId !== "") {
      setPageStatus("editCampaign");
    } else if (nextPage && automationId !== "") {
      setPageStatus("editAutomation");
    } else {
      setPageStatus("newAutomation");
    }
  }, [nextPage, campaignId, automationId]);
  const handleDuplicateNew = async () => {
    var urlParams = [];
    if (campaignId !== "") {
      urlParams.push(`campaign=${campaignId}`);
      urlParams.push(`campaignName=${campaignName}`);
    }
    if (automationId !== "") {
      urlParams.push(`automation=${automationId}`);
      urlParams.push(`automationName=${automationName}`);
    }
    urlParams.push(`next_page=${nextPage}`);
    navigate(
      `/templates/new?${urlParams.reduce((cur, acc) => acc + "&" + cur)}`,
    );
  };

  return (
    <>
      <PageHeading
        title={
          pageStatus === "insertTemplate"
            ? "Choose template to edit"
            : "WhatsApp Templates"
        }
        iconPrimaryAction={<PlusIcon />}
        primaryAction={"Create"}
        handlePrimaryAction={
          campaignId === "" && automationId === ""
            ? async () => {
                navigate("/templates/new");
              }
            : () => handleDuplicateNew()
        }
      />
      <OverflowFrame>
        <TemplateList
          campaignId={campaignId}
          campaignName={campaignName}
          automationId={automationId}
          automationName={automationName}
          nextPage={nextPage}
          pageStatus={pageStatus}
          scenarioId={scenarioId}
          selectedNodeId={selectedNodeId}
        />
      </OverflowFrame>
    </>
  );
}

function TemplateList({
  campaignId,
  campaignName,
  automationId,
  automationName,
  nextPage,
  pageStatus,
  scenarioId,
  selectedNodeId,
}) {
  const { user } = useContext(CustomerContext);
  const [result, getWhatsAppTemplates] = useGlobalAction(
    api.getWhatsAppTemplates,
    {
      customerId: user.customerId,
      limit: "5000",
    },
  );
  const { data, error, fetching } = result;

  useEffect(
    () => {
      getWhatsAppTemplates({
        customerId: user.customerId,
        limit: "5000",
      });
    },
    // C'est pas clean, mais quand il y a une erreur ça refait la requete. au cas où la requete renvoyait rien
    [error],
  );
  if (data) {
    console.log(`Found ${data.length} templates`);
  }

  return (
    <>
      {fetching && <LoadingSpinner />}
      {!fetching && (
        <>
          <PageMarkup
            data={data}
            getWhatsAppTemplates={getWhatsAppTemplates}
            campaignId={campaignId}
            campaignName={campaignName}
            automationId={automationId}
            automationName={automationName}
            nextPage={nextPage}
            pageStatus={pageStatus}
            scenarioId={scenarioId}
            selectedNodeId={selectedNodeId}
          ></PageMarkup>
        </>
      )}
    </>
  );
}

function PageMarkup({
  data,
  getWhatsAppTemplates,
  campaignId,
  campaignName,
  automationId,
  automationName,
  nextPage,
  pageStatus,
  scenarioId,
  selectedNodeId,
}) {
  const navigate = useNavigate();
  const { user } = useContext(CustomerContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const handleDeleteClick = (name) => {
    setTemplateToDelete(name);
    setIsModalPreviewOpen(false);
    setIsModalOpen(true);
  };

  const handleDeleteConfirmation = async () => {
    setIsDeleteLoading(true);

    await api.delete360dialogTemplate({
      templateName: templateToDelete,
      customerId: user.customerId,
    });

    setIsModalOpen(false);
    setIsDeleteLoading(false);
    getWhatsAppTemplates({
      customerId: user.customerId,
      limit: "5000",
    });
  };

  const duplicateTemplate = async (name, category, components) => {
    template.category = category;
    var urlParams = [];
    if (pageStatus === "insertTemplate") {
      urlParams.push(`campaign=${campaignId}`);
      urlParams.push(`campaignName=${campaignName}`);
      urlParams.push(`scenario=${scenarioId}`);
      urlParams.push(`selectedNodeId=${selectedNodeId}`);
      urlParams.push(`next_page=${nextPage}`);
    }
    urlParams.push(`name=${name}`);
    urlParams.push(`category=${category}`);
    const header = components.find((c) => c.type === "HEADER");
    var headerParam = "header=";
    if (header && header.format === "IMAGE") {
      headerParam += "image";
    } else if (header && header.format === "VIDEO") {
      headerParam += "video";
    } else {
      headerParam += "none";
    }
    urlParams.push(headerParam);
    const body = components.find((c) => c.type === "BODY");
    if (body) {
      body.text = body.text.replaceAll("\n", "<br/>").replaceAll("&", "<esp/>");
      urlParams.push(`body=${body.text}`);
    }
    const footer = components.find((c) => c.type === "FOOTER");
    if (footer) {
      urlParams.push(`footer=${footer.text}`);
    }
    const buttons = components.find((c) => c.type === "BUTTONS");
    var quickReplyIndex = 1;

    let templateUrl;
    let templateUrl2;
    if (buttons) {
      try {
        const officialWhatsappTemplate =
          await api.officialWhatsappTemplate.maybeFindFirst({
            filter: {
              customerId: { equals: user.customerId },
              name: { equals: name },
            },
            select: {
              id: true,
              buttonUrlLink: true,
              buttonUrlLink2: true,
            },
          });
        if (officialWhatsappTemplate?.buttonUrlLink) {
          templateUrl = officialWhatsappTemplate.buttonUrlLink;
        }
        if (officialWhatsappTemplate?.buttonUrlLink2) {
          templateUrl2 = officialWhatsappTemplate.buttonUrlLink2;
        }
      } catch (e) {
        console.error(`Unable to find template ${name}, ${e}`);
      }
    }

    let hasUrlButton = false;
    buttons?.buttons?.map(async (b) => {
      if (b.type === "URL" && !hasUrlButton) {
        urlParams.push(`url_button=${b.text}`);
        urlParams.push(
          `url_button_link=${
            templateUrl?.replaceAll("&", "<esp/>") ??
            b.url?.replaceAll("&", "<esp/>")
          }`,
        );
        hasUrlButton = true;
      } else if (b.type === "URL" && hasUrlButton) {
        urlParams.push(`url_button2=${b.text}`);
        urlParams.push(
          `url_button_link2=${
            templateUrl2?.replaceAll("&", "<esp/>") ??
            b.url?.replaceAll("&", "<esp/>")
          }`,
        );
      } else {
        urlParams.push(`reply${quickReplyIndex}=${b.text}`);
        quickReplyIndex++;
      }
    });
    if (campaignId !== "" && pageStatus !== "insertTemplate") {
      urlParams.push(`campaign=${campaignId}`);
      urlParams.push(`campaignName=${campaignName}`);
      urlParams.push(`next_page=campaigns`);
    }
    if (automationId !== "" && pageStatus !== "insertTemplate") {
      urlParams.push(`automation=${automationId}`);
      urlParams.push(`automationName=${automationName}`);
      urlParams.push(`next_page=${nextPage}`);
    }

    navigate(
      `/templates/new?${urlParams.reduce((cur, acc) => acc + "&" + cur)}`,
    );
  };

  const [isModalPreviewOpen, setIsModalPreviewOpen] = useState(false);
  const [template, setTemplate] = useState({});
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [isCarousel, setIsCarousel] = useState(false);

  const handleOpenPreviewClick = async (
    external_id,
    components,
    name,
    status,
    category,
  ) => {
    setIsModalPreviewOpen(true);

    if (components) {
      setIsCarousel(
        components.find((c) => c.type === "CAROUSEL") ? true : false,
      );
      const header = components.find((c) => c.type === "HEADER");
      var headerParam = "";
      if (header && header.format === "IMAGE") {
        headerParam = "image";
      } else if (header && header.format === "VIDEO") {
        headerParam = "video";
      }

      const body = components.find((c) => c.type === "BODY");
      const bodyText = body ? body.text : "";

      const footer = components.find((c) => c.type === "FOOTER");
      const footerText = footer ? footer.text : "";

      const cards = components?.find((c) => c.type === "CAROUSEL")?.cards;
      const limitedTimeOfferText = components?.find(
        (c) => c.type === "LIMITED_TIME_OFFER",
      )?.limited_time_offer.text;

      const limitedTimeOffer = {
        expirationDate: undefined,
        expirationDelayInMinutes: undefined,
        expirationType: "delay",
        text: limitedTimeOfferText,
      };
      let copyCodeButton = null;
      let flowButton = null;

      setTemplate((_template) => ({
        ..._template,
        external_id,
        components,
        name,
        category,
        header: headerParam,
        body: bodyText,
        footer: footerText,
        status,
        isLimitedTimeOffer: !!limitedTimeOfferText,
        limitedTimeOffer,
        cards,
      }));

      const buttons = components.find((c) => c.type === "BUTTONS");
      let quickReplies = [];
      let hasUrlButton = false;
      buttons?.buttons?.forEach((button, index) => {
        if (
          button.type === WhatsappTemplateButtonTypeEnum.URL &&
          !hasUrlButton
        ) {
          setTemplate((prevTemplate) => ({
            ...prevTemplate,
            urlButton: button.text,
            isUrlButtonAtBottom:
              index === buttons.buttons.length - 2 ||
              index === buttons.buttons.length - 1,
          }));
          hasUrlButton = true;
        } else if (
          button.type === WhatsappTemplateButtonTypeEnum.URL &&
          hasUrlButton
        ) {
          setTemplate((prevTemplate) => ({
            ...prevTemplate,
            urlButton2: button.text,
            isUrlButtonAtBottom: index === buttons.buttons.length - 1,
          }));
        } else if (button.type === WhatsappTemplateButtonTypeEnum.COPY_CODE) {
          copyCodeButton = { text: button.example[0] };
        } else if (button.type === WhatsappTemplateButtonTypeEnum.FLOW) {
          flowButton = { text: button.text };
        } else {
          quickReplies.push(button.text);
        }
      });

      setTemplate((prevTemplate) => ({
        ...prevTemplate,
        quickReply1: quickReplies[0] || "",
        quickReply2: quickReplies[1] || "",
        quickReply3: quickReplies[2] || "",
        copyCodeButton,
        flowButton,
      }));
    }
  };

  const handleCancelPreview = () => {
    setIsModalPreviewOpen(false);
    setTemplate({});
  };

  const handleCreateCampaign = async (newTemplate, newTemplateName) => {
    setIsCreateLoading(true);
    let nextPagePath = await createScenarioFromScratch({
      customerId: user.customerId,
      newTemplate: newTemplate,
      newTemplateName: newTemplateName,
    });
    navigate(nextPagePath);
  };

  const rowMarkup = getOptions(data?.waba_templates ?? data)?.map(
    (
      { id, name, category, created_at, status, external_id, components },
      index,
    ) => (
      <TableBody key={`body-${id}`}>
        <TableRow
          id={id}
          key={id}
          position={index}
          onClick={() =>
            handleOpenPreviewClick(
              external_id,
              components,
              name,
              status,
              category,
            )
          }
        >
          <TableCell className="text-zinc-500" key={name}>
            {name}
          </TableCell>
          <TableCell key={`${id}${category}`} className="text-center">
            <Badge
              color={
                (category === WhatsappTemplateCategoryEnum.MARKETING &&
                  "sky") ||
                (category === WhatsappTemplateCategoryEnum.UTILITY &&
                  "amber") ||
                "violet"
              }
            >
              {(category === WhatsappTemplateCategoryEnum.MARKETING &&
                "Marketing") ||
                (category === WhatsappTemplateCategoryEnum.UTILITY &&
                  "Utility") ||
                "Authentication"}
            </Badge>
          </TableCell>
          {created_at && (
            <TableCell className="text-zinc-500" key={`${id}${created_at}`}>
              {new Date(created_at)?.toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
              })}
            </TableCell>
          )}
          <TableCell
            className="text-zinc-500 text-center"
            key={`${id}${status}`}
          >
            <Badge
              color={
                status.toLowerCase() === "approved"
                  ? "lime"
                  : status.toLowerCase() === "rejected"
                    ? "rose"
                    : status.toLowerCase() === "submitted"
                      ? "violet"
                      : status.toLowerCase() === "pending"
                        ? "orange"
                        : "amber"
              }
            >
              {status.toLowerCase()}
            </Badge>
          </TableCell>

          {pageStatus !== "insertTemplate" && (
            <TableCell className="text-center" key={`${id}actions`}>
              <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                <Dropdown>
                  <DropdownButton plain aria-label="More options">
                    <EllipsisHorizontalIcon />
                  </DropdownButton>
                  <DropdownMenu anchor="bottom end">
                    <DropdownItem onClick={() => handleDeleteClick(name)}>
                      Delete
                    </DropdownItem>
                    <DropdownItem
                      onClick={() =>
                        window.open(
                          `https://business.facebook.com/wa/manage/template-details/?id=${external_id}`,
                          "_blank",
                        )
                      }
                    >
                      View on Meta
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </TableCell>
          )}
        </TableRow>
      </TableBody>
    ),
  );

  return (
    <div>
      {(data?.waba_templates?.length ?? data?.length) > 0 ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader className="text-center">Category</TableHeader>
              {data?.waba_templates && <TableHeader>Creation date</TableHeader>}
              <TableHeader className="text-center">Status</TableHeader>
              {pageStatus !== "insertTemplate" && (
                <TableHeader className="text-center">Actions</TableHeader>
              )}
            </TableRow>
          </TableHead>
          {rowMarkup}
        </Table>
      ) : (
        <EmptyState
          heading="You have no template"
          action={() => navigate("/templates/new")}
          icon={<PlusIcon />}
          actionText="Create"
          img="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        />
      )}

      <Dialog
        key="dialog_delete"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <DialogTitle>Delete this template </DialogTitle>
        <DialogBody className="flex-1 overflow-y-auto scrollbar">
          <p>
            Are you sure you want to delete the template{" "}
            <b>{templateToDelete}</b>?
          </p>
          <br />
          <WarningElements template={templateToDelete} user={user} />
        </DialogBody>
        <DialogActions>
          <Button
            color="secondaryButton"
            onClick={() => setIsModalOpen(false)}
            disabled={isDeleteLoading}
            className="min-w-[100px] h-10"
          >
            Cancel
          </Button>
          <Button
            color="primaryButton"
            onClick={() => handleDeleteConfirmation(templateToDelete)}
            className="min-w-[100px] h-10"
            loading={isDeleteLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        key="dialog_preview"
        open={isModalPreviewOpen}
        onClose={handleCancelPreview}
        className="max-h-[80vh] flex flex-col"
      >
        <DialogTitle>
          {(() => {
            switch (pageStatus) {
              case "newCampaign":
                return "Preview";
              case "insertTemplate":
                return "Preview";
              case "editCampaign":
                return `You are duplicating the campaign ${campaignName}`;
              case "editAutomation":
                return `You are duplicating the automation ${automationName}`;
              case "newAutomation":
                return "Create an automation";
            }
          })()}
        </DialogTitle>
        <DialogBody className="flex-1 overflow-y-auto scrollbar p-1">
          {template != {} && isModalPreviewOpen && (
            <>
              <TemplatePreview
                selectedHeader={template.header}
                body={template.body}
                footer={template.footer}
                isLimitedTimeOffer={template.isLimitedTimeOffer}
                limitedTimeOffer={template.limitedTimeOffer}
                flowButton={template.flowButton}
                copyCodeButton={template.copyCodeButton}
                quickReply1={template.quickReply1}
                quickReply2={template.quickReply2}
                quickReply3={template.quickReply3}
                urlButton={template.urlButton}
                urlButton2={template.urlButton2}
                width="100%"
                imageMaxHeight="300px"
                isUrlButtonAtBottom={template.isUrlButtonAtBottom}
                isInPanel={false}
                cards={template.cards}
              />
            </>
          )}
        </DialogBody>
        <DialogActions>
          <Button
            color="primaryButton"
            loading={isCreateLoading}
            disabled={template.status?.toLowerCase() !== "approved"}
            onClick={async () => {
              switch (pageStatus) {
                case "newCampaign":
                  handleCreateCampaign(template, template.name);
                  return;
                case "insertTemplate":
                  duplicateTemplate(
                    template.name,
                    template.components.some(
                      (comp) => comp.type === "LIMITED_TIME_OFFER",
                    )
                      ? "LIMITED_TIME_OFFER"
                      : template.category,
                    template.components,
                  );
                  return;
                case "editCampaign":
                  navigate(
                    `/campaigns/new/template/?campaign=${campaignId}&campaignName=${campaignName}&name=${template.name}`,
                  );
                  return;
                case "editAutomation":
                  navigate(
                    `/automations/new/${nextPage}/?automation=${automationId}&automationName=${automationName}&name=${template.name}`,
                  );
                  return;
                case "newAutomation":
                  navigate(`/automations/new/${nextPage}`);
                  return;
              }
            }}
          >
            {(() => {
              switch (pageStatus) {
                case "newCampaign":
                  return "Create a campaign";
                case "insertTemplate":
                  return "Edit this template";
                case "editCampaign":
                case "editAutomation":
                  return "Use this template";
                case "newAutomation":
                  return "Create an automation";
              }
            })()}
          </Button>
          {pageStatus !== "insertTemplate" && (
            <Dropdown>
              <DropdownButton
                color="secondaryButton"
                aria-label="More options"
                className="rounded-customBorderRadius"
              >
                <EllipsisHorizontalIcon />
              </DropdownButton>
              <DropdownMenu anchor="bottom end">
                {!isCarousel && (
                  <DropdownItem
                    onClick={() =>
                      duplicateTemplate(
                        template.name,
                        template.components.some(
                          (comp) => comp.type === "LIMITED_TIME_OFFER",
                        )
                          ? "LIMITED_TIME_OFFER"
                          : template.category,
                        template.components,
                      )
                    }
                  >
                    Duplicate
                  </DropdownItem>
                )}
                <DropdownItem onClick={() => handleDeleteClick(template.name)}>
                  Delete
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    window.open(
                      `https://business.facebook.com/wa/manage/template-details/?id=${template.external_id}`,
                      "_blank",
                    )
                  }
                >
                  View on Meta
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}

function getOptions(templates) {
  try {
    templates?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  } catch (e) {
    // Tech provider fetching so no need to sort
    console.log("error get templates", e);
  }
  return templates;
}

function WarningElements({ template, user }) {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignItems, setCampaignItems] = useState([]);
  const [automationItems, setAutomationItems] = useState([]);
  const [flowNodeCampaigns, setFlowNodeCampaigns] = useState([]);
  const [flowNodeAutomations, setFlowNodeAutomations] = useState([]);
  const [flowNodeChatbots, setFlowNodeChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const linkClass = "hover:underline hover:cursor-pointer";

  const [{ data: dataTemplateToDelete }] = useFindMany(api.whatsappTemplate, {
    filter: [
      {
        customerId: { equals: user.customerId },
      },
      {
        whatsappTemplateName: { equals: template },
      },
    ],
    select: {
      id: true,
      flowNode: {
        id: true,
        scenario: {
          id: true,
          type: true,
          name: true,
          campaign: {
            id: true,
          },
        },
      },
    },
  });

  useEffect(() => {
    setLoading(true);
    const fetchCampaignsAndScenarios = async () => {
      try {
        if (dataTemplateToDelete && dataTemplateToDelete.length > 0) {
          const campaignPromises = dataTemplateToDelete.map(
            (whatsappTemplate) =>
              api.campaign.findMany({
                filter: [
                  { whatsappTemplateId: { equals: whatsappTemplate.id } },
                ],
              }),
          );
          const campaignResults = await Promise.all(campaignPromises);
          const allCampaigns = campaignResults.flat();
          setCampaigns(allCampaigns);

          const extractedScenarios =
            dataTemplateToDelete
              .map((item) => item.flowNode?.scenario)
              .filter((scenario) => scenario !== undefined) || [];

          setCampaignItems(
            allCampaigns.filter((campaign) => campaign.nextPage === null),
          );
          setAutomationItems(
            allCampaigns.filter((campaign) => campaign.nextPage !== null),
          );

          setFlowNodeCampaigns(
            extractedScenarios.filter(
              (scenario) => scenario.type === "campaign",
            ),
          );
          setFlowNodeAutomations(
            extractedScenarios.filter(
              (scenario) => scenario.type === "automation",
            ),
          );
          setFlowNodeChatbots(
            extractedScenarios.filter(
              (scenario) => scenario.type === "chatbot",
            ),
          );
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignsAndScenarios();
  }, [dataTemplateToDelete]);

  if (loading)
    return (
      <div className="h-[100px]">
        <LoadingSpinner />
      </div>
    );
  if (error) return <div>Error: {error.message}</div>;

  return (
    <>
      {(campaigns.length > 0 ||
        flowNodeCampaigns.length > 0 ||
        flowNodeAutomations.length > 0 ||
        flowNodeChatbots.length > 0) && (
        <>
          <p>
            If you delete this template, the following campaigns, automations,
            and chatbots will no longer function correctly:
          </p>

          {(campaignItems.length > 0 || flowNodeCampaigns.length > 0) && (
            <>
              <p>
                <b>Campaigns</b>
              </p>
              <ul>
                {campaignItems.map((campaign) => (
                  <li key={campaign.id}>
                    <Link
                      className={linkClass}
                      onClick={() =>
                        window.open(`/campaigns/edit/${campaign.id}`, "_blank")
                      }
                    >
                      {campaign.name}
                    </Link>
                  </li>
                ))}
                {flowNodeCampaigns.map((campaign) => (
                  <li key={campaign.id}>
                    <Link
                      className={linkClass}
                      onClick={() =>
                        window.open(
                          `/campaigns/edit/${campaign.campaign.id}`,
                          "_blank",
                        )
                      }
                    >
                      {campaign.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}

          {(automationItems.length > 0 || flowNodeAutomations.length > 0) && (
            <>
              <p>
                <b>Automations</b>
              </p>
              <ul>
                {automationItems.map((automation) => (
                  <li key={automation.id}>
                    <Link
                      className={linkClass}
                      onClick={() =>
                        window.open(
                          `/automations/edit/${automation.id}`,
                          "_blank",
                        )
                      }
                    >
                      {automation.name}
                    </Link>
                  </li>
                ))}
                {flowNodeAutomations.map((automation) => (
                  <li key={automation.id}>
                    <Link
                      className={linkClass}
                      onClick={() =>
                        window.open(
                          `/automations/edit/${automation.campaign.id}`,
                          "_blank",
                        )
                      }
                    >
                      {automation.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}

          {flowNodeChatbots.length > 0 && (
            <>
              <p>
                <b>Chatbots</b>
              </p>
              <ul>
                {flowNodeChatbots.map((chatbot) => (
                  <li key={chatbot.id}>
                    <Link
                      className={linkClass}
                      onClick={
                        (() => window.open(`/chatbots/edit/${chatbot.id}`),
                        "_blank")
                      }
                    >
                      {chatbot.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </>
  );
}
